import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CircleNotch, UserPlus, X, PaperPlaneTilt, Prohibit, ArrowCounterClockwise, Trash } from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * Support Executives — who can sign in to this console and receive an assigned ticket.
 *
 * Support ticket assignment shipped and was verified, and was unusable: the product could create
 * exactly one administrator, the row the seed inserts. This page is the missing half.
 *
 * Every destructive control here can, in principle, remove somebody's access to a console with no
 * root account and no out-of-band recovery. The server refuses to leave nobody able to sign in
 * (assertNotLastActiveAdmin, checked inside the write transaction) and refuses self-action — this
 * UI states those rules rather than hiding the buttons, so a refusal reads as a rule and not a bug.
 */

interface Admin {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: 'active' | 'suspended' | 'invited';
  twoFaEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

const STATUS: Record<Admin['status'], { label: string; className: string; hint: string }> = {
  active:    { label: 'Active',    className: 'bg-plat-success-soft text-plat-success-text', hint: 'Can sign in and be assigned tickets.' },
  invited:   { label: 'Invited',   className: 'bg-plat-info-soft text-plat-info-text',       hint: 'Invitation sent — cannot sign in until the password is set.' },
  suspended: { label: 'Suspended', className: 'bg-plat-danger-soft text-plat-danger-text',   hint: 'Cannot sign in. Keeps any tickets already assigned.' },
};

function fmt(s: string | null) {
  return s ? new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
}

// ── Invite ────────────────────────────────────────────────────────────────────

function InviteModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  const mutation = useMutation({
    mutationFn: () => platformApi.post('/team', { firstName, lastName, email }),
    onSuccess: () => {
      toast.success('Invitation sent. They set their own password from the link.');
      onDone(); onClose();
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  const ready = firstName.trim() && lastName.trim() && /.+@.+\..+/.test(email);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/60 px-4 py-8">
      <ScrollLock />
      <div role="dialog" aria-modal="true" aria-label="Invite a Support Executive"
           className="my-auto w-full max-w-lg rounded-xl border border-plat-border bg-plat-surface p-6 shadow-2xl">
        <div className="mb-5 flex items-start justify-between">
          <div>
            <h3 className="text-[15px] font-semibold text-plat-fg">Invite a Support Executive</h3>
            <p className="mt-0.5 text-sm text-plat-muted">
              They receive a single-use link to set their own password. No account is active until they use it.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close"
                  className="rounded p-1 text-plat-muted hover:bg-plat-hover hover:text-plat-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-plat-focus">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="team-first" className="mb-1.5 block text-xs font-medium text-plat-secondary">First name</label>
              <input id="team-first" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                     className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none" />
            </div>
            <div>
              <label htmlFor="team-last" className="mb-1.5 block text-xs font-medium text-plat-secondary">Last name</label>
              <input id="team-last" value={lastName} onChange={(e) => setLastName(e.target.value)}
                     className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none" />
            </div>
          </div>
          <div>
            <label htmlFor="team-email" className="mb-1.5 block text-xs font-medium text-plat-secondary">Work email</label>
            <input id="team-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="name@jurivoxa.local"
                   className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none" />
          </div>
          <p className="text-xs text-plat-muted">
            A Support Executive can read every chambers&apos; support tickets and open the investigation
            tools. Invite only people who should have that access.
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose}
                    className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-secondary hover:bg-plat-hover">
              Cancel
            </button>
            <button onClick={() => mutation.mutate()} disabled={!ready || mutation.isPending}
                    className="inline-flex items-center gap-2 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:cursor-not-allowed disabled:bg-plat-neutral-soft disabled:text-plat-disabled">
              {mutation.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4" /> Send invitation</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Confirm a destructive change ──────────────────────────────────────────────

function ConfirmModal({
  title, body, confirmLabel, pending, onCancel, onConfirm,
}: {
  title: string; body: string; confirmLabel: string; pending: boolean;
  onCancel: () => void; onConfirm: () => void;
}) {
  // `items-start` + `overflow-y-auto` on the overlay and `my-auto` on the panel, matching the
  // invite dialog above. Centring with clipped overflow looks identical until the panel is taller
  // than the window — then it pushes its own Cancel/Confirm buttons off both edges with nothing to
  // scroll. This dialog carries the lockout and self-action explanations, so its body is
  // variable-length, and a short laptop window is enough to reach that state.
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/60 px-4 py-8">
      <ScrollLock />
      <div role="dialog" aria-modal="true" aria-label={title}
           className="my-auto w-full max-w-md rounded-xl border border-plat-border bg-plat-surface p-6 shadow-2xl">
        <h3 className="text-[15px] font-semibold text-plat-fg">{title}</h3>
        <p className="mt-2 text-sm text-plat-secondary">{body}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel}
                  className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-secondary hover:bg-plat-hover">
            Cancel
          </button>
          {/*
            The disabled state names tokens rather than dimming with opacity. `opacity-70` fades
            the label INTO its own red fill — white-on-danger is a deliberate pairing, and
            compositing both at 70% destroys the ratio between them, so "Working…" greys out to
            near-unreadable exactly while the operator is waiting to see it. `plat-neutral-soft` /
            `plat-disabled` are a measured pair (3.55:1 light, 3.96:1 dark — globals.css) and are
            what every other disabled control in this console uses, including the invite button
            in this same file.
          */}
          <button onClick={onConfirm} disabled={pending}
                  className="rounded-lg bg-plat-danger-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-danger-solid-hover disabled:cursor-not-allowed disabled:bg-plat-neutral-soft disabled:text-plat-disabled">
            {pending ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PlatformTeamPage() {
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirming, setConfirming] = useState<{ admin: Admin; kind: 'suspend' | 'remove' } | null>(null);

  const { data: admins = [], isLoading } = useQuery<Admin[]>({
    queryKey: ['platform', 'team'],
    queryFn: async () => {
      const res = await platformApi.get('/team');
      return res.data?.data ?? res.data;
    },
  });

  const { data: me } = useQuery<{ id: string }>({
    queryKey: ['platform', 'me'],
    queryFn: async () => {
      const res = await platformApi.get('/auth/me');
      return (res.data?.data ?? res.data)?.admin ?? res.data?.data ?? res.data;
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['platform', 'team'] });
    // A newly active executive becomes assignable immediately; a suspended one stops being so.
    queryClient.invalidateQueries({ queryKey: ['platform', 'support-assignees'] });
    queryClient.invalidateQueries({ queryKey: ['platform', 'support'] });
  };

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'suspended' }) =>
      platformApi.patch(`/team/${id}/status`, { status }),
    onSuccess: (_r, v) => { toast.success(v.status === 'suspended' ? 'Access suspended' : 'Access restored'); setConfirming(null); refresh(); },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => platformApi.delete(`/team/${id}`),
    onSuccess: () => { toast.success('Administrator removed and signed out everywhere'); setConfirming(null); refresh(); },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => platformApi.post(`/team/${id}/resend-invite`),
    onSuccess: () => toast.success('Invitation resent'),
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  const activeCount = admins.filter((a) => a.status === 'active').length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-plat-fg">Support Executives</h2>
          <p className="mt-0.5 text-sm text-plat-muted">
            Who can sign in to this console and be assigned a support ticket.
          </p>
        </div>
        <button onClick={() => setInviteOpen(true)}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover">
          <UserPlus className="h-4 w-4" /> Invite executive
        </button>
      </div>

      {/*
        The lockout rule, stated up front. The server enforces it; saying so here means a refusal
        reads as a deliberate rule rather than a failure, and nobody wastes time retrying it.
      */}
      {activeCount <= 1 && !isLoading && (
        <div className="flex items-start gap-2 rounded-xl border border-plat-warning-line bg-plat-warning-soft/40 px-4 py-3">
          <Prohibit className="mt-0.5 h-4 w-4 shrink-0 text-plat-warning-text" />
          <p className="text-sm text-plat-secondary">
            Only one administrator can currently sign in. They cannot be suspended or removed —
            this console has no recovery path if nobody can sign in. Invite and activate a second
            executive first.
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16"><CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" /></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-plat-border bg-plat-surface">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-plat-border">
                {['Executive', 'Status', '2FA', 'Last sign-in', ''].map((h, i) => (
                  <th key={h || i} className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-plat-border">
              {admins.map((a) => {
                const isMe = a.id === me?.id;
                const lastActive = a.status === 'active' && activeCount <= 1;
                return (
                  <tr key={a.id} className="hover:bg-plat-hover">
                    <td className="px-5 py-3.5">
                      <div className="font-medium text-plat-fg">
                        {a.firstName} {a.lastName}
                        {isMe && <span className="ml-2 rounded bg-plat-neutral-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase text-plat-secondary">You</span>}
                      </div>
                      <div className="text-xs text-plat-muted">{a.email}</div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${STATUS[a.status].className}`}
                            title={STATUS[a.status].hint}>
                        {STATUS[a.status].label}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-plat-secondary">{a.twoFaEnabled ? 'On' : '—'}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-plat-muted">{fmt(a.lastLoginAt)}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        {a.status === 'invited' && (
                          <button onClick={() => resendMutation.mutate(a.id)}
                                  aria-label={`Resend the invitation to ${a.email}`}
                                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-plat-info-text hover:bg-plat-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-plat-focus">
                            <PaperPlaneTilt className="h-3.5 w-3.5" /> Resend
                          </button>
                        )}
                        {a.status === 'suspended' && (
                          <button onClick={() => statusMutation.mutate({ id: a.id, status: 'active' })}
                                  disabled={isMe}
                                  aria-label={`Restore access for ${a.email}`}
                                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-plat-success-text hover:bg-plat-hover disabled:cursor-not-allowed disabled:text-plat-disabled focus:outline-none focus-visible:ring-2 focus-visible:ring-plat-focus">
                            <ArrowCounterClockwise className="h-3.5 w-3.5" /> Restore
                          </button>
                        )}
                        {a.status === 'active' && (
                          <button onClick={() => setConfirming({ admin: a, kind: 'suspend' })}
                                  disabled={isMe || lastActive}
                                  title={isMe ? 'You cannot suspend your own account' : lastActive ? 'The last administrator who can sign in cannot be suspended' : undefined}
                                  aria-label={`Suspend ${a.email}`}
                                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-plat-warning-text hover:bg-plat-hover disabled:cursor-not-allowed disabled:text-plat-disabled focus:outline-none focus-visible:ring-2 focus-visible:ring-plat-focus">
                            <Prohibit className="h-3.5 w-3.5" /> Suspend
                          </button>
                        )}
                        <button onClick={() => setConfirming({ admin: a, kind: 'remove' })}
                                disabled={isMe || lastActive}
                                title={isMe ? 'You cannot remove your own account' : lastActive ? 'The last administrator who can sign in cannot be removed' : undefined}
                                aria-label={`Remove ${a.email}`}
                                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-plat-danger-text hover:bg-plat-hover disabled:cursor-not-allowed disabled:text-plat-disabled focus:outline-none focus-visible:ring-2 focus-visible:ring-plat-focus">
                          <Trash className="h-3.5 w-3.5" /> Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} onDone={refresh} />}

      {confirming && (
        <ConfirmModal
          title={confirming.kind === 'suspend'
            ? `Suspend ${confirming.admin.firstName} ${confirming.admin.lastName}?`
            : `Remove ${confirming.admin.firstName} ${confirming.admin.lastName}?`}
          body={confirming.kind === 'suspend'
            ? 'They cannot sign in until restored. Tickets already assigned to them stay assigned, and the ticket still shows them as the owner so nothing silently changes hands.'
            : 'They are signed out everywhere immediately and cannot sign in again. Their record is retained so the Activity Log still shows what they did.'}
          confirmLabel={confirming.kind === 'suspend' ? 'Suspend access' : 'Remove administrator'}
          pending={statusMutation.isPending || removeMutation.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => confirming.kind === 'suspend'
            ? statusMutation.mutate({ id: confirming.admin.id, status: 'suspended' })
            : removeMutation.mutate(confirming.admin.id)}
        />
      )}
    </div>
  );
}
